import { useState } from 'react'
import { api, ApiRequestError } from '../lib/api'
import { useAuthStore } from '../stores/authStore'

interface ApiKeyFixInlineProps {
  /**
   * Called after a new key is verified and saved. The caller resumes the
   * stalled action from here (retry extraction, re-run analysis/assembly,
   * re-run the upload) so the user lands back where they were with their work
   * intact — no navigation away to Settings.
   */
  onResolved: () => void | Promise<void>
}

type VerifyState = 'idle' | 'verifying' | 'valid' | 'invalid'

/**
 * In-place key recovery. Reuses the same verify + save endpoints as the
 * Settings and onboarding key screens, so there is one capture/validation path.
 * Rendered inside the invalid-key gate on whichever surface stalled.
 */
export default function ApiKeyFixInline({ onResolved }: ApiKeyFixInlineProps) {
  const fetchUser = useAuthStore((s) => s.fetchUser)

  const [rawKey, setRawKey] = useState('')
  const [showKey, setShowKey] = useState(false)
  const [verifyState, setVerifyState] = useState<VerifyState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const handleVerify = async () => {
    if (!rawKey.trim()) return
    setVerifyState('verifying')
    setError(null)
    try {
      const res = await api.post<{ valid: boolean; message: string }>('/api/v1/user/apikey/verify', {
        raw_key: rawKey,
      })
      setVerifyState(res.valid ? 'valid' : 'invalid')
      if (!res.valid) setError(res.message)
    } catch {
      setVerifyState('invalid')
      setError('We could not verify that key. Check it and try again.')
    }
  }

  const handleSaveAndResume = async () => {
    if (verifyState !== 'valid') return
    setSaving(true)
    setError(null)
    try {
      await api.put('/api/v1/user/apikey', { raw_key: rawKey })
      await fetchUser()
      await onResolved()
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Could not save your key.')
      setSaving(false)
    }
  }

  return (
    <div className="mt-4 w-full max-w-md text-left">
      <label htmlFor="fix-apikey" className="block text-sm font-medium text-on-surface">
        Anthropic API key
      </label>
      <div className="relative mt-1 flex items-center">
        <input
          id="fix-apikey"
          type={showKey ? 'text' : 'password'}
          value={rawKey}
          onChange={(e) => {
            setRawKey(e.target.value)
            setVerifyState('idle')
            setError(null)
          }}
          placeholder="sk-ant-…"
          autoComplete="off"
          spellCheck={false}
          className="w-full rounded-lg border border-outline-variant bg-surface px-3 py-2 pr-16 font-mono text-sm text-on-surface focus:border-primary-container focus:outline-none"
        />
        <button
          type="button"
          onClick={() => setShowKey((v) => !v)}
          className="absolute right-2 text-xs font-semibold uppercase tracking-wider text-secondary hover:text-on-surface"
        >
          {showKey ? 'Hide' : 'Show'}
        </button>
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}

      <div className="mt-3">
        {verifyState === 'valid' ? (
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSaveAndResume()}
            className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-sm font-medium text-on-primary transition hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save key & continue'}
          </button>
        ) : (
          <button
            type="button"
            disabled={!rawKey.trim() || verifyState === 'verifying'}
            onClick={() => void handleVerify()}
            className="inline-flex items-center justify-center rounded-lg border border-on-surface px-4 py-2 text-sm font-semibold text-on-surface transition hover:bg-surface-container-high disabled:opacity-40"
          >
            {verifyState === 'verifying' ? 'Validating…' : 'Validate key'}
          </button>
        )}
      </div>
    </div>
  )
}
